import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
    Activity,
    ArrowRight,
    Zap,
    Shield,
    ChevronDown,
    Layers,
    Clock,
    Database,
    Sparkles,
    CheckCircle2,
    SlidersHorizontal,
    TrendingUp,
} from 'lucide-react'
import { Navbar } from '../components/landing/Navbar'
import { HeroCard } from '../components/landing/HeroCard'
import { CONFIG, LANDING_PAGE_CONFIG, COLORS } from '../config/constants'
import { useAuth } from '../auth/AuthContext'

export const Route = createFileRoute('/')({ component: LandingPage })

function LandingPage() {
    const { isAuthenticated } = useAuth()
    const navigate = useNavigate()
    const [selectedSymbol, setSelectedSymbol] = useState<string>(
        CONFIG.SYMBOLS[0] || 'NIFTY'
    )
    const [activeArchitectureTab, setActiveArchitectureTab] = useState<
        'chain' | 'levels' | 'history'
    >('chain')
    const [openFaq, setOpenFaq] = useState<number | null>(0)

    const handleAuthAction = () => {
        if (isAuthenticated) {
            navigate({ to: '/home' })
        } else {
            navigate({ to: '/login', search: { redirect: '/' } })
        }
    }

    const simData =
        (LANDING_PAGE_CONFIG.SIMULATION as Record<string, any>)[selectedSymbol] ||
        LANDING_PAGE_CONFIG.SIMULATION.NIFTY!

    return (
        <div className="min-h-screen bg-[#fafbfc] dark:bg-[#060b0b] text-slate-900 dark:text-slate-100 font-sans selection:bg-[#005451]/15 selection:text-[#005451] dark:selection:bg-[#005451]/40 dark:selection:text-teal-200 overflow-x-hidden transition-colors duration-300">
            {/* --- Subtle Ambient Atmospheric Glows --- */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none transform-gpu opacity-70 dark:opacity-40">
                <div className="absolute -top-[15%] left-[10%] w-[50vw] h-[50vw] bg-gradient-to-br from-emerald-100/50 via-teal-100/30 to-transparent dark:from-[#005451]/30 dark:via-teal-900/20 blur-[140px] rounded-full mix-blend-multiply dark:mix-blend-screen" />
                <div className="absolute top-[35%] -right-[15%] w-[45vw] h-[45vw] bg-gradient-to-bl from-teal-100/40 via-cyan-100/20 to-transparent dark:from-teal-900/25 dark:via-emerald-950/20 blur-[140px] rounded-full mix-blend-multiply dark:mix-blend-screen" />
                <div className="absolute -bottom-[10%] left-[25%] w-[55vw] h-[40vw] bg-gradient-to-tr from-emerald-100/40 via-teal-100/20 to-transparent dark:from-[#005451]/20 dark:via-slate-900/20 blur-[160px] rounded-full mix-blend-multiply dark:mix-blend-screen" />
            </div>

            {/* --- Subtle Engineering Grid --- */}
            <div className="fixed inset-0 z-0 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] dark:bg-[radial-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:24px_24px] opacity-60 pointer-events-none" />

            {/* --- Sticky Glass Navbar --- */}
            <Navbar />

            {/* --- 1. HERO SECTION --- */}
            <section className="relative z-10 pt-32 pb-16 lg:pt-40 lg:pb-24 px-6" id="cockpit">
                <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-14 items-center min-h-[75vh]">
                    {/* Left Column: Hero Copy & Actions */}
                    <div className="space-y-7 text-center lg:text-left">
                        {/* Live Terminal Eyebrow & Index Quick Selector */}
                        <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2.5">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-slate-900/80 border border-slate-200/90 dark:border-white/10 shadow-xs text-[11px] font-mono font-bold tracking-wider text-slate-600 dark:text-slate-300 uppercase">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                                </span>
                                Terminal Mode
                            </div>

                            {/* Symbol Switchers from CONFIG.SYMBOLS */}
                            <div className="inline-flex p-1 rounded-full bg-white dark:bg-slate-900/80 border border-slate-200/90 dark:border-white/10 shadow-xs">
                                {CONFIG.SYMBOLS.map((sym) => (
                                    <button
                                        key={sym}
                                        type="button"
                                        onClick={() => setSelectedSymbol(sym)}
                                        className={`px-3 py-1 rounded-full text-[11px] font-mono font-bold uppercase tracking-wider transition-all duration-200 ${
                                            selectedSymbol === sym
                                                ? 'bg-[#005451] text-white shadow-xs'
                                                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                        }`}
                                    >
                                        {sym}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Punchy Institutional Headline */}
                        <div className="space-y-3">
                            <h1 className="text-4xl sm:text-6xl lg:text-6xl font-black tracking-tight text-slate-900 dark:text-white leading-[1.08]">
                                Institutional Options <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#005451] via-[#006e6a] to-[#008f89] dark:from-teal-400 dark:via-teal-300 dark:to-emerald-400">
                                    Intelligence in Real Time
                                </span>
                            </h1>
                            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 max-w-xl mx-auto lg:mx-0 font-medium leading-relaxed">
                                High-velocity options chain decoding, automated support & resistance levels, dynamic gamma shift tracking, and 1-minute historical intraday analytics for active index traders.
                            </p>
                        </div>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3.5 justify-center lg:justify-start pt-1">
                            <button
                                onClick={handleAuthAction}
                                className="group relative px-8 py-4 bg-[#005451] hover:bg-[#004240] text-white rounded-2xl font-bold text-base shadow-lg shadow-[#005451]/25 hover:shadow-xl hover:shadow-[#005451]/35 transition-all duration-300 hover:-translate-y-0.5 active:scale-95 flex items-center justify-center gap-2.5 overflow-hidden"
                            >
                                <Zap size={18} className="fill-current text-teal-200" />
                                <span>{isAuthenticated ? 'Enter Trading Terminal' : 'Launch Options Terminal'}</span>
                                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                            </button>

                            <a
                                href="#features"
                                className="px-7 py-4 bg-white dark:bg-slate-900/90 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200/90 dark:border-white/10 rounded-2xl font-bold text-base shadow-xs hover:border-slate-300 dark:hover:border-white/20 transition-all duration-300 hover:-translate-y-0.5 active:scale-95 flex items-center justify-center gap-2"
                            >
                                <SlidersHorizontal size={17} className="text-[#005451] dark:text-teal-400" />
                                <span>Explore Engine</span>
                            </a>
                        </div>

                        {/* Sleek Trust & Telemetry Strip */}
                        <div className="pt-2 flex flex-wrap items-center justify-center lg:justify-start gap-5 text-slate-500 dark:text-slate-400 text-xs font-semibold">
                            <div className="flex items-center gap-1.5">
                                <CheckCircle2 size={15} className="text-emerald-600 dark:text-emerald-400" />
                                <span>Sub-2s Feed Latency</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <CheckCircle2 size={15} className="text-emerald-600 dark:text-emerald-400" />
                                <span>Dynamic ATM Calibration</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <CheckCircle2 size={15} className="text-emerald-600 dark:text-emerald-400" />
                                <span>1-Min Intraday Backup</span>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Hero Terminal Cockpit Card */}
                    <div className="relative">
                        <HeroCard symbol={selectedSymbol} />
                    </div>
                </div>
            </section>

            {/* --- 2. TELEMETRY & PERFORMANCE METRICS RIBBON --- */}
            <section className="relative z-10 py-6 px-6" id="performance">
                <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4 p-6 sm:p-8 rounded-3xl bg-white dark:bg-slate-900/80 border border-slate-200/90 dark:border-white/10 shadow-lg shadow-slate-200/50 dark:shadow-none">
                    {LANDING_PAGE_CONFIG.STATS.map((stat, idx) => (
                        <div
                            key={idx}
                            className={`space-y-1 text-center sm:text-left ${
                                idx !== 0 ? 'sm:border-l sm:border-slate-100 dark:sm:border-white/10 sm:pl-6' : ''
                            }`}
                        >
                            <div className="text-2xl sm:text-3xl font-black font-mono text-[#005451] dark:text-teal-400">
                                {stat.value}
                            </div>
                            <div className="text-xs font-bold text-slate-800 dark:text-slate-200 tracking-tight">
                                {stat.label}
                            </div>
                            <div className="text-[11px] text-slate-400 dark:text-slate-400 font-medium">
                                {stat.desc}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* --- 3. CORE CAPABILITIES (3 PILLARS) --- */}
            <section className="py-20 lg:py-28 px-6 relative z-10" id="features">
                <div className="max-w-7xl mx-auto space-y-16">
                    {/* Section Header */}
                    <div className="text-center max-w-2xl mx-auto space-y-3">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 text-emerald-800 dark:text-emerald-300 text-[10px] font-mono font-bold uppercase tracking-wider">
                            <Sparkles size={12} /> ARCHITECTURAL ADVANTAGE
                        </div>
                        <h2 className="text-3xl sm:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
                            Engineered for Clarity & Speed
                        </h2>
                        <p className="text-slate-600 dark:text-slate-400 text-base font-medium">
                            Three quantitative pillars that eliminate noise and provide direct institutional edge.
                        </p>
                    </div>

                    {/* 3 Balanced Ultra-Premium Pillar Cards */}
                    <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
                        {LANDING_PAGE_CONFIG.PILLARS.map((pillar) => (
                            <div
                                key={pillar.number}
                                className="group relative p-8 rounded-3xl bg-white dark:bg-slate-900/80 border border-slate-200/90 dark:border-white/10 shadow-sm hover:shadow-xl hover:border-[#005451]/30 dark:hover:border-teal-500/30 transition-all duration-300 flex flex-col justify-between space-y-6"
                            >
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-3xl font-black font-mono text-slate-200 dark:text-slate-800 group-hover:text-[#005451]/40 dark:group-hover:text-teal-500/40 transition-colors">
                                            {pillar.number}
                                        </span>
                                        <span className="text-[10px] font-mono font-extrabold px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 group-hover:bg-[#005451]/10 dark:group-hover:bg-teal-500/10 group-hover:text-[#005451] dark:group-hover:text-teal-400 transition-colors">
                                            {pillar.tag}
                                        </span>
                                    </div>
                                    <h3 className="text-xl font-black text-slate-900 dark:text-white leading-snug">
                                        {pillar.title}
                                    </h3>
                                    <p className="text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
                                        {pillar.desc}
                                    </p>
                                </div>

                                <div className="pt-4 border-t border-slate-100 dark:border-white/5 flex items-center justify-between text-xs font-bold text-[#005451] dark:text-teal-400">
                                    <span>Learn Specification</span>
                                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- 4. INTERACTIVE ARCHITECTURE SHOWCASE --- */}
            <section className="py-20 px-6 relative z-10 bg-slate-100/50 dark:bg-[#070e0e]/50 border-y border-slate-200/80 dark:border-white/10" id="architecture">
                <div className="max-w-6xl mx-auto space-y-12">
                    <div className="text-center max-w-2xl mx-auto space-y-3">
                        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
                            Quantitative Workspace Architecture
                        </h2>
                        <p className="text-slate-600 dark:text-slate-400 text-base font-medium">
                            Inspect how each algorithmic component processes market data.
                        </p>
                    </div>

                    {/* Architecture Selector Navigation */}
                    <div className="flex flex-wrap justify-center gap-2">
                        {LANDING_PAGE_CONFIG.SHOWCASE_MODULES.map((mod) => (
                            <button
                                key={mod.id}
                                type="button"
                                onClick={() => setActiveArchitectureTab(mod.id as any)}
                                className={`px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-200 border ${
                                    activeArchitectureTab === mod.id
                                        ? 'bg-[#005451] text-white border-[#005451] shadow-md shadow-[#005451]/20'
                                        : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-slate-800'
                                }`}
                            >
                                {mod.title}
                            </button>
                        ))}
                    </div>

                    {/* Showcase Content Card */}
                    <div className="rounded-3xl bg-white dark:bg-slate-900/80 border border-slate-200/90 dark:border-white/10 shadow-xl p-6 sm:p-10">
                        {activeArchitectureTab === 'chain' && (
                            <div className="space-y-6">
                                <div className="space-y-1">
                                    <h3 className="text-xl font-black text-slate-900 dark:text-white">
                                        Sub-2s Strategy Chain Engine
                                    </h3>
                                    <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">
                                        Calculates dynamic ATM strike in real time using{' '}
                                        <code className="text-[#005451] dark:text-teal-300 font-mono bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-xs">
                                            ROUND(Index_LTP / Strike_GAP, 0) * Strike_GAP
                                        </code>{' '}
                                        with automated strike range centering.
                                    </p>
                                </div>

                                <div className="grid sm:grid-cols-3 gap-4 pt-2">
                                    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-white/10">
                                        <div className="text-[10px] font-mono font-bold text-slate-400 uppercase">
                                            Index Step Calibration
                                        </div>
                                        <div className="text-lg font-black font-mono text-slate-900 dark:text-white mt-1">
                                            NIFTY: 50 | SENSEX: 100
                                        </div>
                                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                            Fully automated step intervals
                                        </div>
                                    </div>
                                    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-white/10">
                                        <div className="text-[10px] font-mono font-bold text-slate-400 uppercase">
                                            Streaming Feed
                                        </div>
                                        <div className="text-lg font-black font-mono text-emerald-700 dark:text-emerald-400 mt-1">
                                            High-Speed Live Feed
                                        </div>
                                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                            Refreshes silently every 2s
                                        </div>
                                    </div>
                                    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-white/10">
                                        <div className="text-[10px] font-mono font-bold text-slate-400 uppercase">
                                            ATM Centering
                                        </div>
                                        <div className="text-lg font-black font-mono text-[#005451] dark:text-teal-400 mt-1">
                                            Zero Viewport Drift
                                        </div>
                                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                            Strikes remain pinned on ATM
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeArchitectureTab === 'levels' && (
                            <div className="space-y-6">
                                <div className="space-y-1">
                                    <h3 className="text-xl font-black text-slate-900 dark:text-white">
                                        Algorithmic Support, Resistance & Shift
                                    </h3>
                                    <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">
                                        Top-3 Call/Put OI clustering identifies exact market boundary walls. Shift level is calculated as{' '}
                                        <code className="text-[#005451] dark:text-teal-300 font-mono bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-xs">
                                            (Resistance Strike + Support Strike) / 2
                                        </code>
                                        .
                                    </p>
                                </div>

                                <div className="grid sm:grid-cols-3 gap-4 pt-2 font-mono">
                                    <div className="p-4 rounded-2xl bg-rose-50/70 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/40">
                                        <div className="text-[10px] font-bold text-rose-600 dark:text-rose-400 uppercase">
                                            Resistance Strike
                                        </div>
                                        <div
                                            className="text-2xl font-black mt-1"
                                            style={{ color: COLORS.RESISTANCE }}
                                        >
                                            {simData.resistanceStrike}
                                        </div>
                                        <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                                            Top 3 CE OI Minimum Strike
                                        </div>
                                    </div>
                                    <div className="p-4 rounded-2xl bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/40">
                                        <div className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase">
                                            Support Strike
                                        </div>
                                        <div
                                            className="text-2xl font-black mt-1"
                                            style={{ color: COLORS.SUPPORT }}
                                        >
                                            {simData.supportStrike}
                                        </div>
                                        <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                                            Top 3 PE OI Maximum Strike
                                        </div>
                                    </div>
                                    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-white/10">
                                        <div className="text-[10px] font-bold text-[#005451] dark:text-teal-400 uppercase">
                                            Calculated Shift
                                        </div>
                                        <div className="text-2xl font-black text-[#005451] dark:text-teal-400 mt-1">
                                            {simData.shift}
                                        </div>
                                        <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                                            Midpoint Pivot Baseline
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeArchitectureTab === 'history' && (
                            <div className="space-y-6">
                                <div className="space-y-1">
                                    <h3 className="text-xl font-black text-slate-900 dark:text-white">
                                        1-Minute Intraday Cloud Snapshots
                                    </h3>
                                    <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">
                                        Every minute of market action is archived continuously to high-speed cloud infrastructure.
                                    </p>
                                </div>

                                <div className="grid sm:grid-cols-3 gap-4 pt-2">
                                    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-white/10">
                                        <div className="text-[10px] font-mono font-bold text-slate-400 uppercase">
                                            Snapshot Cadence
                                        </div>
                                        <div className="text-lg font-black font-mono text-slate-900 dark:text-white mt-1">
                                            Every 60 Seconds
                                        </div>
                                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                            Synchronized during market hours
                                        </div>
                                    </div>
                                    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-white/10">
                                        <div className="text-[10px] font-mono font-bold text-slate-400 uppercase">
                                            Zero Flickering
                                        </div>
                                        <div className="text-lg font-black font-mono text-emerald-700 dark:text-emerald-400 mt-1">
                                            Silent Updates
                                        </div>
                                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                            Smooth background cache reload
                                        </div>
                                    </div>
                                    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-white/10">
                                        <div className="text-[10px] font-mono font-bold text-slate-400 uppercase">
                                            Historical Modes
                                        </div>
                                        <div className="text-lg font-black font-mono text-[#005451] dark:text-teal-400 mt-1">
                                            Instant Replay
                                        </div>
                                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                            Replay any historical trading session
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* --- 5. FREQUENTLY ASKED QUESTIONS --- */}
            <section className="py-20 lg:py-28 px-6 relative z-10">
                <div className="max-w-3xl mx-auto space-y-12">
                    <div className="text-center space-y-3">
                        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
                            Platform Specifications
                        </h2>
                        <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base font-medium">
                            Key architectural details regarding the options quantitative terminal.
                        </p>
                    </div>

                    <div className="space-y-3">
                        {LANDING_PAGE_CONFIG.FAQS.map((faq, idx) => (
                            <div
                                key={idx}
                                className="rounded-2xl border border-slate-200/90 dark:border-white/10 bg-white dark:bg-slate-900/80 overflow-hidden shadow-xs transition-all"
                            >
                                <button
                                    type="button"
                                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                                    className="w-full p-5 sm:p-6 text-left font-bold text-slate-900 dark:text-white flex items-center justify-between gap-4"
                                >
                                    <span className="text-sm sm:text-base">{faq.question}</span>
                                    <ChevronDown
                                        className={`shrink-0 transition-transform duration-200 text-[#005451] dark:text-teal-400 ${
                                            openFaq === idx ? 'rotate-180' : ''
                                        }`}
                                        size={18}
                                    />
                                </button>
                                {openFaq === idx && (
                                    <div className="px-5 pb-5 sm:px-6 sm:pb-6 text-xs sm:text-sm text-slate-600 dark:text-slate-300 font-medium leading-relaxed border-t border-slate-100 dark:border-white/5 pt-3">
                                        {faq.answer}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- 6. ULTRA-PREMIUM FINAL CTA BANNER --- */}
            <section className="py-20 px-6 relative z-10">
                <div className="max-w-5xl mx-auto rounded-3xl bg-gradient-to-br from-[#005451] via-[#004240] to-[#002b2a] text-white p-8 sm:p-14 shadow-2xl shadow-[#005451]/20 relative overflow-hidden text-center space-y-6">
                    {/* Ambient subtle backdrop light */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-white/10 blur-[100px] pointer-events-none" />

                    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/15 border border-white/20 text-teal-100 text-xs font-mono font-bold uppercase tracking-wider backdrop-blur-md">
                        <Sparkles size={13} /> READY FOR INSTITUTIONAL EXECUTION
                    </div>

                    <h2 className="text-3xl sm:text-5xl font-black tracking-tight leading-tight">
                        Experience the Market with <br /> Algorithmic Precision
                    </h2>

                    <p className="text-sm sm:text-base text-teal-100 max-w-xl mx-auto font-medium leading-relaxed">
                        Join disciplined traders analyzing real-time options positioning, gamma shifts, and institutional key levels.
                    </p>

                    <div className="pt-2">
                        <button
                            type="button"
                            onClick={handleAuthAction}
                            className="group inline-flex items-center justify-center px-8 py-4 text-base font-bold text-slate-900 bg-white hover:bg-slate-100 rounded-2xl shadow-xl hover:-translate-y-0.5 active:scale-95 transition-all duration-300"
                        >
                            <span>Access Quantitative Terminal</span>
                            <ArrowRight
                                className="ml-2 group-hover:translate-x-1 transition-transform text-[#005451]"
                                size={18}
                            />
                        </button>
                    </div>
                </div>
            </section>

            {/* --- 7. MINIMALIST FOOTER --- */}
            <footer className="border-t border-slate-200/80 dark:border-white/10 bg-white dark:bg-[#060b0b] py-10 px-6">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#005451] rounded-lg shadow-sm">
                            <Activity className="text-white" size={17} strokeWidth={2.5} />
                        </div>
                        <span className="text-sm font-bold tracking-tight text-slate-900 dark:text-white">
                            Options<span className="text-[#005451] dark:text-teal-400">Oracle</span>{' '}
                            <span className="text-slate-400 font-normal ml-1">© 2026</span>
                        </span>
                    </div>

                    {/* Operational Status Pill */}
                    <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 text-[11px] font-mono font-bold">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        <span>ALL SYSTEMS OPERATIONAL</span>
                    </div>

                    <div className="flex gap-6 text-xs font-semibold text-slate-500 dark:text-slate-400">
                        <a href="#cockpit" className="hover:text-[#005451] dark:hover:text-teal-400 transition-colors">
                            Terminal
                        </a>
                        <a href="#features" className="hover:text-[#005451] dark:hover:text-teal-400 transition-colors">
                            Capabilities
                        </a>
                        <a href="#architecture" className="hover:text-[#005451] dark:hover:text-teal-400 transition-colors">
                            Architecture
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    )
}
